import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Typography, useTheme } from '@mui/material';
import supabase from '../supabaseClient';
import dayjs from 'dayjs';
import { calculateProgress } from '../utils/quranUtils';
import TimePeriodFilter from './ui/TimePeriodFilter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const QuranProgressLineChart = ({ classId, teacherId, progressMode = 'verses' }) => {
  const [progressData, setProgressData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month');
  const theme = useTheme();

  // Handle view mode change
  const handleViewModeChange = (event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  };

  useEffect(() => {
    const fetchProgressData = async () => {
      if (!classId) return;

      try {
        setLoading(true);

        // Get current date
        const now = dayjs();
        let startDate;

        // Calculate start date based on view mode
        switch (viewMode) {
          case 'week':
            startDate = now.subtract(7, 'day').format('YYYY-MM-DD');
            break;
          case 'month':
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD');
            break;
          case '6months':
            startDate = now.subtract(180, 'day').format('YYYY-MM-DD');
            break;
          case 'year':
            startDate = now.subtract(365, 'day').format('YYYY-MM-DD');
            break;
          default:
            startDate = now.subtract(30, 'day').format('YYYY-MM-DD');
        }

        let query = supabase
          .from('student_quran_progress')
          .select(`
            id,
            date,
            starting_surah,
            starting_verse,
            ending_surah,
            ending_verse,
            assignment_status,
            student_id,
            students(first_name, last_name)
          `)
          .gte('date', startDate)
          .in('assignment_status', ['full', 'partial'])
          .order('date', { ascending: true });

        if (classId === 'all') {
          if (teacherId) {
            const { data: classesData, error: classesError } = await supabase
              .from('classes')
              .select('id')
              .eq('teacher_id', teacherId);

            if (classesError) {
              console.error('Error fetching teacher classes:', classesError);
              return;
            }

            if (classesData && classesData.length > 0) {
              const classIds = classesData.map((cls) => cls.id);
              query = query.in('class_id', classIds);
            }
          }
        } else {
          query = query.eq('class_id', classId);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching progress data:', error);
          return;
        }

        // Process data by student and date
        const studentProgress = {};

        data.forEach((record) => {
          const studentId = record.student_id;
          const studentName = record.students
            ? `${record.students.first_name} ${record.students.last_name}`
            : `Student ID: ${studentId}`;
          const date = record.date;

          if (!studentProgress[studentId]) {
            studentProgress[studentId] = {
              name: studentName,
              dailyProgress: {},
              cumulativeProgress: 0,
            };
          }

          // Calculate progress (verses or pages) for this day
          const progress = calculateProgress(
            record.starting_surah,
            record.starting_verse,
            record.ending_surah,
            record.ending_verse,
            progressMode
          );

          // Add to daily progress
          if (!studentProgress[studentId].dailyProgress[date]) {
            studentProgress[studentId].dailyProgress[date] = 0;
          }
          studentProgress[studentId].dailyProgress[date] += progress;
        });

        // Convert daily progress to cumulative progress
        const processedData = Object.values(studentProgress).map(student => {
          const dates = Object.keys(student.dailyProgress).sort();
          let cumulative = 0;
          const progressPoints = dates.map(date => {
            cumulative += student.dailyProgress[date];
            return {
              date,
              dailyProgress: student.dailyProgress[date],
              totalProgress: cumulative
            };
          });

          return {
            name: student.name,
            data: progressPoints
          };
        });

        setProgressData(processedData);
      } catch (error) {
        console.error('Error in fetchProgressData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgressData();
  }, [classId, teacherId, viewMode, progressMode]);

  // Get unit label based on progress mode
  const unitName = progressMode === 'pages' ? 'pages' : 'verses'
  const unitLabel = progressMode === 'pages' ? 'Total Pages Completed' : 'Total Verses Completed'

  const chartData = useMemo(() => {
    if (progressData.length === 0) return null;

    const colorPalette = [
      'rgba(75, 192, 192, 1)',
      'rgba(255, 99, 132, 1)',
      'rgba(255, 205, 86, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(153, 102, 255, 1)',
      'rgba(255, 159, 64, 1)',
    ];

    // Get all unique dates across all students
    const allDates = [...new Set(
      progressData.flatMap(student => student.data.map(d => d.date))
    )].sort();

    return {
      labels: allDates.map(date => dayjs(date).format('MMM D')),
      datasets: progressData.map((student, index) => ({
        label: student.name,
        data: allDates.map(date => {
          const point = student.data.find(d => d.date === date);
          return point ? point.totalProgress : null;
        }),
        borderColor: colorPalette[index % colorPalette.length],
        backgroundColor: colorPalette[index % colorPalette.length],
        tension: 0.4,
        fill: false,
        spanGaps: true,
      })),
    };
  }, [progressData, unitName]);

  const chartOptions = useMemo(() => {
    // Get all unique dates for tooltip access
    const allDates = [...new Set(
      progressData.flatMap(student => student.data.map(d => d.date))
    )].sort();

    return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.palette.text.primary,
          font: {
            family: theme.typography.fontFamily,
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: `Cumulative Quran Progress Over Time (by ${unitName})`,
        color: theme.palette.text.primary,
        font: {
          family: theme.typography.fontFamily,
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            if (context.parsed.y === null) return null;
            
            const student = progressData[context.datasetIndex];
            const date = allDates[context.dataIndex];
            const point = student?.data.find(d => d.date === date);
            
            const labels = [`${context.dataset.label}: ${context.parsed.y} total ${unitName}`];
            if (point?.dailyProgress) {
              labels.push(`Daily Progress: ${point.dailyProgress} ${unitName}`);
            }
            return labels;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
          maxRotation: 45,
          minRotation: 45
        },
      },
      y: {
        title: {
          display: true,
          text: unitLabel,
          color: theme.palette.text.primary,
        },
        grid: {
          color: theme.palette.divider,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
    },
  };
  }, [theme, progressData, unitName, unitLabel]);

  const chartContainerStyle = {
    height: 400,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  };

  if (loading) {
    return (
      <Box>
        <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />
        <Box sx={chartContainerStyle}>
          <Typography variant="body1" color="text.secondary">
            Loading progress data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <TimePeriodFilter value={viewMode} onChange={handleViewModeChange} />

      {!chartData || progressData.length === 0 ? (
        <Box sx={chartContainerStyle}>
          <Typography variant="body1" color="text.secondary">
            No progress data available for the selected period.
          </Typography>
        </Box>
      ) : (
        <>

          <Box sx={chartContainerStyle}>
            <Line options={chartOptions} data={chartData} />
          </Box>

          <Box sx={{ mt: 2, p: 2, backgroundColor: theme.palette.background.default, borderRadius: theme.shape.borderRadius }}>
            <Typography variant="subtitle2" gutterBottom>
              About this Chart:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This chart shows the cumulative progress of each student over time. Each line represents a student's total {unitName} completed, 
              with steeper slopes indicating faster progress. Hover over points to see both total {unitName} and daily progress.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default QuranProgressLineChart;